/** Date-only arithmetic stays in UTC so DST never moves a payment date. */
export type Cadence = "once" | "weekly" | "biweekly" | "monthly";
export type PlanItem = {
  key: string;
  name: string;
  amount: number;
  kind: "income" | "payment";
  date: string;
  cadence: Cadence;
  accountId: string | null;
  accountLabel: string;
  estimated: boolean;
  active: boolean;
  source?: string;
  observedAt?: string;
  note?: string;
};
export type ReviewedCalendar = {
  reviewedAt: number;
  replacesSourceKeys: string[];
  completed: string[];
  items: PlanItem[];
};
export type PlanEvent = PlanItem & { eventDate: string; completed: boolean };
export function validDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
export function dayAdd(value: string, days: number): string {
  const d = new Date(`${value}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
export function monthAdd(value: string, months: number): string {
  const d = new Date(`${value}T12:00:00Z`);
  const end = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months + 1, 0),
  );
  return new Date(
    Date.UTC(
      end.getUTCFullYear(),
      end.getUTCMonth(),
      Math.min(d.getUTCDate(), end.getUTCDate()),
      12,
    ),
  )
    .toISOString()
    .slice(0, 10);
}
export function occurrences(
  items: PlanItem[],
  start: string,
  end: string,
  completed = new Set<string>(),
): PlanEvent[] {
  const events: PlanEvent[] = [];
  for (const item of items) {
    if (!item.active || !validDate(item.date)) continue;
    const anchor = new Date(item.date);
    const first = new Date(start);
    let index =
      item.cadence === "monthly"
        ? Math.max(
            0,
            (first.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
              first.getUTCMonth() -
              anchor.getUTCMonth(),
          )
        : item.cadence === "once"
          ? 0
          : Math.max(
              0,
              Math.floor(
                (first.getTime() - anchor.getTime()) /
                  86400000 /
                  (item.cadence === "weekly" ? 7 : 14),
              ),
            );
    for (let safety = 0; safety < 400; safety++, index++) {
      const date =
        item.cadence === "monthly"
          ? monthAdd(item.date, index)
          : item.cadence === "once"
            ? item.date
            : dayAdd(item.date, index * (item.cadence === "weekly" ? 7 : 14));
      if (date > end) break;
      if (date >= start)
        events.push({
          ...item,
          eventDate: date,
          completed: completed.has(`${item.key}:${date}`),
        });
      if (item.cadence === "once") break;
    }
  }
  return events.sort(
    (a, b) =>
      a.eventDate.localeCompare(b.eventDate) || a.name.localeCompare(b.name),
  );
}
export function periodRange(anchor: string, period: "day" | "week" | "month") {
  if (period === "day") return { start: anchor, end: anchor };
  if (period === "week") {
    const offset = (new Date(anchor).getUTCDay() + 6) % 7;
    const start = dayAdd(anchor, -offset);
    return { start, end: dayAdd(start, 6) };
  }
  const start = `${anchor.slice(0, 7)}-01`;
  return { start, end: dayAdd(monthAdd(start, 1), -1) };
}

/** Complete calendar weeks expose nearby due dates without moving their recurrence. */
export function calendarRange(anchor: string) {
  const month = periodRange(anchor, "month");
  const start = dayAdd(
    month.start,
    -((new Date(month.start).getUTCDay() + 6) % 7),
  );
  const end = dayAdd(
    month.end,
    6 - ((new Date(month.end).getUTCDay() + 6) % 7),
  );
  return { start, end };
}

/** Replace only reviewed source identities, retaining unrelated rent, payroll and history. */
export function applyReviewedCalendar(
  items: PlanItem[],
  completed: string[],
  review?: ReviewedCalendar,
) {
  if (!review) return { items, completed };
  const replaced = new Set(review.replacesSourceKeys);
  const result = new Map(
    items
      .filter((item) => !replaced.has(item.key))
      .map((item) => [item.key, item]),
  );
  for (const item of review.items) result.set(item.key, item);
  return {
    items: [...result.values()],
    completed: [...new Set([...completed, ...review.completed])],
  };
}
